import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isFetchingFeatureFlags } from '@core/selectors/classroom';
import {
  Area,
  Box,
  Chip,
  Icons,
  Separator,
  Text,
  TextField,
  useTheme,
  Container,
} from '@ublend-npm/aulaui-core';

import { AulaState } from '@core/types/state';
import { FetchResultsParams } from '@core/actions/omniSearch/types';
import {
  fetchResults as fetchResultsAction,
  clearResults as clearResultsAction,
  setSearchText as setSearchTextAction,
  setFilter as setFilterAction,
} from '@core/actions/omniSearch';
import pushRoute from '@app/utils/routing';
import { viewportHeightWithOffset } from '@app/utils/viewportHeight';
import SearchNoResults from '@app/components/presentational/common/NoResults/SearchNoResults';

import { OmniSearchLoading } from './OmniSearchLoading';
import { OmniSearchResult } from './OmniSearchResult';
import { OmniSearchTip } from './OmniSearchTip';

const FILTER_LABELS: Record<FetchResultsParams['type'], string> = {
  content: 'All',
  posts: 'Posts',
  comments: 'Comments',
  materials: 'Materials',
  messages: 'Direct Messages',
};

const DEBOUNCE_MS = 500;
export const PAGE_SIZE = 20;

export const OmniSearch = () => {
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout>();
  const searchResultsMaxHeight = viewportHeightWithOffset(358);
  const theme = useTheme();

  const loading = useSelector((state: AulaState) => state.omniSearch.loading);
  const results = useSelector((state: AulaState) => state.omniSearch.results);
  const searchText = useSelector(
    (state: AulaState) => state.omniSearch.searchText
  );
  const filter = useSelector(
    (state: AulaState) => state.omniSearch.selectedFilterType
  );
  const resultValues = results ? Object.values(results) : [];

  const spacesNames = useSelector((state: AulaState) =>
    Object.values(state.data.classRooms).reduce(
      (acc, classRoom) => ({
        ...acc,
        [classRoom.objectId]: classRoom.name,
      }),
      {} as Record<string, string>
    )
  );
  const conversationsTitles = useSelector((state: AulaState) =>
    Object.values(state.data.inboxv2.conversations).reduce(
      (acc, conversation) => ({
        ...acc,
        [conversation.id]: conversation.title,
      }),
      {} as Record<string, string>
    )
  );
  const footerMap = { ...spacesNames, ...conversationsTitles };

  const dispatch = useDispatch();
  const fetchResults = fetchResultsAction(dispatch);
  const clearResults = clearResultsAction(dispatch);
  const setSearchTextDispatch = setSearchTextAction(dispatch);
  const setFilterDispatch = setFilterAction(dispatch);

  const handleClearClick = () => setSearchTextDispatch({ searchText: '' });
  const handleSearchTextChange = (text: string) =>
    setSearchTextDispatch({ searchText: text });
  const handleFilterChange = (newFilter: FetchResultsParams['type']) =>
    setFilterDispatch({ selectedFilterType: newFilter });

  useEffect(() => {
    clearResults({ loading: false });
    prevResultsLength.current = 0;
    setNoMoreResults(false);
    if (!searchText) return;
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const debTimeout = setTimeout(() => {
      fetchResults({
        query: searchText,
        type: filter,
        pageSize: PAGE_SIZE,
        skip: 0,
        selectedTabIndex: 0,
      });
      setDebounceTimeout(undefined);
    }, DEBOUNCE_MS);
    setDebounceTimeout(debTimeout);
  }, [searchText, filter]);

  const containerRef = useRef<HTMLDivElement>(null);
  const prevResultsLength = useRef(0);
  const [noMoreResults, setNoMoreResults] = useState(false);

  // Infinite loading triggered by scroll
  useEffect(() => {
    const currResultsLength = Object.keys(results || {}).length;

    if (loading || currResultsLength === 0) {
      return;
    } else if (prevResultsLength.current + PAGE_SIZE > currResultsLength) {
      setNoMoreResults(true);
      return;
    } else {
      prevResultsLength.current = currResultsLength;
    }

    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 1000) {
        fetchResults({
          query: searchText,
          type: filter,
          pageSize: PAGE_SIZE,
          skip: Object.keys(results).length,
          selectedTabIndex: 0,
        });
      }
    };

    container.addEventListener('scroll', handleScroll);

    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, [loading]);

  // Changing document title
  useEffect(() => {
    document.title = `Search - Aula`;
  }, []);

  // wait until feature flags are ready before redirecting.
  const { webAppFtOmniSearch202506 } = useFlags();
  const fetchingFeatureFlags = useSelector((state: AulaState) =>
    isFetchingFeatureFlags(state)
  );
  const isFlagValueReady = typeof webAppFtOmniSearch202506 === 'boolean';
  const isFlagsLoading = fetchingFeatureFlags || !isFlagValueReady;

  useEffect(() => {
    if (isFlagsLoading) return;
    if (!webAppFtOmniSearch202506) pushRoute('/dashboard');
  }, [isFlagsLoading, webAppFtOmniSearch202506]);

  if (isFlagsLoading) return null;
  return (
    <Container
      sx={{
        px: { xs: '0.5rem', md: 0 },
        pt: 0,
      }}
    >
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          margin: 'auto',
        }}
      >
        <TextField
          label="Search"
          value={searchText}
          sx={{
            marginBottom: -2,
            '& .MuiInputBase-root': {
              backgroundColor: theme.palette.background.paper,
            },
          }}
          onChange={(e) => handleSearchTextChange(e.target.value)}
          leftElement={searchText === '' ? <Icons.Search /> : null}
          rightElement={
            searchText !== '' ? (
              <Box
                component="button"
                onClick={handleClearClick}
                sx={{
                  cursor: 'pointer',
                  border: 'none',
                  background: 'none',
                  color: 'inherit',
                  padding: 0,
                }}
                aria-label="Clear search"
              >
                <Icons.Close />
              </Box>
            ) : null
          }
        />
        {!loading && !debounceTimeout && !searchText ? (
          <OmniSearchTip />
        ) : (
          <Area rounded>
            <Text
              kind="p3"
              sx={{ marginBottom: 2, color: theme.palette.text.secondary }}
            >
              Filter by:
            </Text>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.keys(FILTER_LABELS).map(
                (f: FetchResultsParams['type']) => (
                  <Chip
                    key={f}
                    kind={f === filter ? 'primary' : 'default'}
                    onClick={() => handleFilterChange(f)}
                    label={FILTER_LABELS[f]}
                    role="tab"
                  />
                )
              )}
            </Box>
            <Separator size="thin" sx={{ marginTop: 2 }} />
            {resultValues.length > 0 ? (
              <Text
                kind="p2"
                sx={{ color: theme.palette.text.secondary, marginY: 2 }}
              >
                Showing {noMoreResults ? '' : 'first '}
                {resultValues.length} results
              </Text>
            ) : null}
            <Box
              ref={containerRef}
              sx={{ overflowY: 'auto', maxHeight: searchResultsMaxHeight }}
            >
              {resultValues.length === 0 && (loading || debounceTimeout) ? (
                <OmniSearchLoading quantity={3} />
              ) : null}
              {!loading &&
              !debounceTimeout &&
              searchText &&
              resultValues.length === 0 ? (
                <SearchNoResults />
              ) : null}
              {resultValues.length > 0
                ? resultValues.map((result) => (
                    <OmniSearchResult
                      key={result.id}
                      result={result}
                      footerMap={footerMap}
                    />
                  ))
                : null}
              {resultValues.length > 0 && loading ? (
                <OmniSearchLoading quantity={3} />
              ) : null}
              {resultValues.length > 0 && noMoreResults ? (
                <Text
                  kind="p1"
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.text.secondary,
                  }}
                >
                  This is the end of your search.
                </Text>
              ) : null}
            </Box>
          </Area>
        )}
      </Box>
    </Container>
  );
};

OmniSearch.displayName = 'OmniSearch';
